import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit'
import { getData, patchData, getRemoteUrl, deleteData } from './request'
import { addCases } from './utils'
import { getLoginToken } from './store'
import { getCurrentSessionGeneration, sessionInvalidated } from './session'

function initialState() {
    return {
        activities: undefined,
        activitiesLoading: false,
        selectedActivity: undefined,
        selectedActivityTrace: undefined,
        requestError: undefined,
        sessionGeneration: 0,
    }
}

function setActivitiesData(state, { payload }) {
    if (payload.sessionGeneration !== state.sessionGeneration) return
    state.activities = payload.activities

    if (!state.selectedActivity && state.selectedActivity !== payload.firstActivity) {
        state.selectedActivity = payload.firstActivity
        state.selectedActivityTrace = payload.activityTrace
    }

    state.activitiesLoading = false
}

function setActivitiesError(state, { payload }) {
    if (payload && payload.sessionGeneration !== state.sessionGeneration) return
    state.activities = []
    state.activitiesLoading = false
    state.selectedActivity = undefined
    state.selectedActivityTrace = undefined
    state.requestError = (payload && payload.error) || undefined
}

function setActivitiesLoading(state) {
    state.activitiesLoading = true
}

function setSelectedActivityTrace(state, { payload }) {
    if (payload.sessionGeneration !== state.sessionGeneration) return
    state.selectedActivityTrace = payload.trace
}

function reqActivities() {
    const sessionGeneration = getCurrentSessionGeneration()
    const loginToken = getLoginToken()
    let params = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${loginToken}`
        }
    }
    const urlWithArgs = getRemoteUrl("activities?") + new URLSearchParams({'count': 1200, 'orderby':'start_time desc'})
    return getData(urlWithArgs, params)
        .then(activities => {
            if (activities.length > 0) {
                const firstActivity = activities[0]
                return reqActivityTrace(firstActivity.id).then(activityTrace => {
                    return {
                        sessionGeneration,
                        activities: activities,
                        firstActivity: firstActivity,
                        activityTrace: activityTrace.trace}
                })
            }
            return {sessionGeneration: sessionGeneration, activities: activities}
        })
}

function reqActivityTrace(activityId) {
    const sessionGeneration = getCurrentSessionGeneration()
    const loginToken = getLoginToken()
    let params = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${loginToken}`
        }
    }
    return getData(getRemoteUrl("activities/" + activityId + "/trace"), params)
        .then(json => ({ sessionGeneration, trace: json.trace }))
}

export function reqChangeActivityName(activityId, newName) {
    let params = {
        name: newName
    }

    return patchData(getRemoteUrl("activities/" + activityId), params, getLoginToken())
}

export function reqDeleteActivity(activityId) {
    return deleteData(getRemoteUrl("activities/" + activityId), null, getLoginToken())
}


export const asyncReqActivities = createAsyncThunk(
    'asyncReqActivities',
    async (_, { rejectWithValue }) => {
        try {
            return await reqActivities()
        } catch (error) {
            return rejectWithValue({ sessionGeneration: getCurrentSessionGeneration(), error })
        }
    }
)
export const asyncReqActivityTrace = createAsyncThunk(
    'asyncReqActivityTrace',
    async (activityId, { rejectWithValue }) => {
        try {
            return await reqActivityTrace(activityId)
        } catch (error) {
            return rejectWithValue({ sessionGeneration: getCurrentSessionGeneration(), error })
        }
    }
)

export const activitiesSlice = createSlice({
    name: 'activities',
    initialState: initialState(),

    reducers: {
        setActivities(state, { payload }) {
            setActivitiesData(state, { payload: { ...payload, sessionGeneration: state.sessionGeneration } })
        },
        setActivityName(state, {payload}) {
            const oldState = current(state)
            let newState = {
                ...oldState,
                activities: oldState.activities.map(activity => {
                    if (activity.id !== payload.id) {
                        return activity
                    }
        
                    return {
                        ...activity,
                        name:payload.name
                    }
              })
            }
            return newState
        },
        setSelectedActivity(state, {payload}) {
            const oldState = current(state)
            let newState = {
                ...oldState,
                selectedActivity: payload,
                selectedActivityTrace: []
            }
            return newState
        },
        setSelectedActivityTrace(state, {payload}) {
            const oldState = current(state)
            let newState = {
                ...oldState,
                selectedActivityTrace: payload
            }
            return newState
        }
    },

    extraReducers: builder => {
        addCases(builder, asyncReqActivities, setActivitiesData, setActivitiesLoading, setActivitiesError)
        addCases(builder, asyncReqActivityTrace, setSelectedActivityTrace, undefined, setActivitiesError)
        builder.addCase(sessionInvalidated, (state, action) => ({
            ...initialState(),
            sessionGeneration: action.payload,
        }))
    }
})

export const { setActivities, setActivityName, setSelectedActivity } = activitiesSlice.actions